import React, { useEffect, useState, useCallback } from "react";
import { Avatar, theme } from "antd";
import { GiftOutlined, CalendarOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { getBackendUrl } from "../../utils/env";

dayjs.locale("es");

/* ─── ESTILOS DINÁMICOS ───────────────────────────────────────────────────── */
const makeStyles = (t) => `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=Mulish:wght@400;500;600&display=swap');

  .bd-root {
    font-family: 'Mulish', sans-serif;
    background: ${t.colorBgLayout};
    min-height: 100vh;
    padding: clamp(24px, 5vw, 48px) clamp(16px, 4vw, 40px) 80px;
    max-width: 900px;
    margin: 0 auto;
  }

  /* ── Header ── */
  .bd-header {
    text-align: center;
    margin-bottom: 48px;
    animation: fadeUp 0.45s ease both;
  }
  .bd-header-icon {
    font-size: 36px;
    display: block;
    margin-bottom: 12px;
  }
  .bd-title {
    font-family: 'Syne', sans-serif;
    font-size: clamp(30px, 8vw, 52px);
    font-weight: 800;
    color: ${t.colorText};
    line-height: 1.05;
    margin: 0 0 8px;
  }
  .bd-subtitle {
    font-size: 14px;
    color: ${t.colorTextSecondary};
  }

  /* ── Sección label ── */
  .bd-section-label {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 3px;
    text-transform: uppercase;
    color: ${t.colorTextTertiary};
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .bd-section-label::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${t.colorBorderSecondary};
  }

  /* ── Hoy: cards destacadas ── */
  .bd-today-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;
    margin-bottom: 48px;
  }
  .bd-today-card {
    background: ${t.colorBgContainer};
    border-radius: 22px;
    padding: 28px 20px 22px;
    text-align: center;
    position: relative;
    overflow: hidden;
    border: 1.5px solid ${t.colorBorderSecondary};
    animation: fadeUp 0.4s ease both;
    transition: transform 0.2s, box-shadow 0.2s;
  }
  .bd-today-card:hover {
    transform: translateY(-5px);
  }
  .bd-today-card-bg {
    position: absolute;
    inset: 0;
    pointer-events: none;
    opacity: 0.06;
  }
  .bd-today-card-crown {
    font-size: 26px;
    display: block;
    margin-bottom: -8px;
    position: relative;
    z-index: 1;
    animation: float 2.5s ease-in-out infinite;
  }
  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50%       { transform: translateY(-5px); }
  }
  .bd-today-avatar {
    position: relative;
    display: inline-block;
    margin-bottom: 14px;
    z-index: 1;
  }
  .bd-today-badge {
    position: absolute;
    bottom: 2px;
    right: 2px;
    font-size: 16px;
    line-height: 1;
  }
  .bd-today-name {
    font-family: 'Syne', sans-serif;
    font-size: 16px;
    font-weight: 700;
    color: ${t.colorText};
    margin: 0 0 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .bd-today-date {
    font-size: 12px;
    color: ${t.colorTextSecondary};
    margin-bottom: 12px;
  }
  .bd-today-pill {
    display: inline-block;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1px;
    padding: 4px 12px;
    border-radius: 100px;
    text-transform: uppercase;
  }
  .bd-today-age {
    font-family: 'Syne', sans-serif;
    font-size: 13px;
    font-weight: 700;
    color: ${t.colorTextTertiary};
    margin-top: 8px;
  }

  /* ── Mes ── */
  .bd-month-block { margin-bottom: 40px; }
  .bd-month-title {
    font-family: 'Syne', sans-serif;
    font-size: 20px;
    font-weight: 800;
    color: ${t.colorText};
    margin: 0;
  }

  /* ── Grid de personas ── */
  .bd-person-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
    gap: 10px;
  }
  .bd-person-card {
    background: ${t.colorBgContainer};
    border: 1px solid ${t.colorBorderSecondary};
    border-radius: 16px;
    padding: 16px 10px 14px;
    text-align: center;
    cursor: default;
    transition: all 0.18s;
    animation: fadeUp 0.35s ease both;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
  }
  .bd-person-card:hover {
    border-color: ${t.colorPrimary}60;
    background: ${t.colorPrimaryBg};
    transform: translateY(-3px);
  }
  .bd-person-name {
    font-size: 12px;
    font-weight: 600;
    color: ${t.colorText};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
    text-align: center;
  }
  .bd-person-date {
    font-size: 11px;
    color: ${t.colorTextTertiary};
  }
  .bd-days-pill {
    font-size: 10px;
    font-weight: 700;
    padding: 3px 9px;
    border-radius: 100px;
    margin-top: 2px;
    letter-spacing: 0.3px;
  }
  .bd-days-soon {
    background: ${t.colorWarningBg};
    color: ${t.colorWarning};
    border: 1px solid ${t.colorWarningBorder};
  }
  .bd-days-normal {
    background: ${t.colorFillAlter};
    color: ${t.colorTextSecondary};
    border: 1px solid ${t.colorBorderSecondary};
  }

  /* ── Skeleton ── */
  @keyframes shimmer {
    0%   { background-position: -600px 0; }
    100% { background-position:  600px 0; }
  }
  .skeleton {
    border-radius: 10px;
    background: linear-gradient(
      90deg,
      ${t.colorFillAlter} 25%,
      ${t.colorFillSecondary} 50%,
      ${t.colorFillAlter} 75%
    );
    background-size: 600px;
    animation: shimmer 1.4s infinite linear;
  }

  /* ── Empty ── */
  .bd-empty {
    text-align: center;
    padding: 60px 24px;
    color: ${t.colorTextTertiary};
    font-size: 14px;
  }
  .bd-empty-icon { font-size: 40px; margin-bottom: 12px; opacity: 0.4; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

/* ─── HELPERS ─────────────────────────────────────────────────────────────── */
const useToday = () => dayjs();

function daysUntil(birthdate, today) {
  const base = dayjs(birthdate).year(today.year()).startOf("day");
  const next = base.isBefore(today.startOf("day")) ? base.add(1, "year") : base;
  return next.diff(today.startOf("day"), "day");
}

function calcAge(birthdate, today) {
  return today.diff(dayjs(birthdate), "year");
}

function isTodayBirthday(birthdate, today) {
  return dayjs(birthdate).format("MM-DD") === today.format("MM-DD");
}

/* Colores por género para la card de hoy */
function todayCardColors(gender) {
  return gender === "femenino"
    ? { accent: "#F472B6", bg: "#FDF2F8", pill: "#BE185D" }
    : { accent: "#FBBF24", bg: "#FFFBEB", pill: "#92400E" };
}

/* ─── COMPONENTE PRINCIPAL ────────────────────────────────────────────────── */
export default function BirthdayList() {
  const { token }      = theme.useToken();
  const today          = useToday();
  const currentUserId  = localStorage.getItem("userId");
  const css            = makeStyles(token);

  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const tkn = localStorage.getItem("token");
        const { data } = await axios.get(`${getBackendUrl()}api/birthdays.php`, {
          headers: { Authorization: `Bearer ${tkn}` },
        });
        setUsers(data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* Ordenar por proximidad */
  const sorted = [...users].sort(
    (a, b) => daysUntil(a.birthdate, today) - daysUntil(b.birthdate, today)
  );

  const todayBirthdays = sorted.filter((u) => isTodayBirthday(u.birthdate, today));

  /* Agrupar por mes (excluyendo hoy) */
  const rest = sorted.filter((u) => !isTodayBirthday(u.birthdate, today));
  const byMonth = rest.reduce((acc, u) => {
    const m = dayjs(u.birthdate).format("MMMM");
    if (!acc[m]) acc[m] = [];
    acc[m].push(u);
    return acc;
  }, {});

  const monthOrder = Object.keys(byMonth).sort(
    (a, b) => daysUntil(byMonth[a][0].birthdate, today) - daysUntil(byMonth[b][0].birthdate, today)
  );

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="bd-root">
        <style>{css}</style>
        <div className="bd-header">
          <span className="bd-header-icon">🎂</span>
          <h1 className="bd-title">Cumpleaños</h1>
          <p className="bd-subtitle">Cargando...</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(130px,1fr))", gap: 10 }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="skeleton" style={{ height: 140 }} />
          ))}
        </div>
      </div>
    );
  }

  /* ── Vista ── */
  return (
    <div className="bd-root">
      <style>{css}</style>

      {/* Header */}
      <div className="bd-header">
        <span className="bd-header-icon">🎂</span>
        <h1 className="bd-title">Cumpleaños</h1>
        <p className="bd-subtitle">Celebremos juntos a nuestra comunidad.</p>
      </div>

      {/* ── HOY ── */}
      {todayBirthdays.length > 0 && (
        <div style={{ marginBottom: 48 }}>
          <div className="bd-section-label">
            <span>🎉</span> Celebrando hoy
          </div>
          <div className="bd-today-grid">
            {todayBirthdays.map((u, i) => {
              const age    = calcAge(u.birthdate, today);
              const cols   = todayCardColors(u.gender);
              const isMe   = String(u.id) === String(currentUserId);
              return (
                <div
                  key={u.id}
                  className="bd-today-card"
                  style={{
                    border: `2px solid ${cols.accent}`,
                    boxShadow: `0 8px 28px -8px ${cols.accent}55`,
                    animationDelay: `${i * 0.07}s`,
                  }}
                >
                  {/* Fondo suave */}
                  <div
                    className="bd-today-card-bg"
                    style={{ background: cols.bg }}
                  />

                  {/* Corona flotante */}
                  <span className="bd-today-card-crown">👑</span>

                  {/* Avatar */}
                  <div className="bd-today-avatar">
                    <Avatar
                      src={u.avatar ? `${getBackendUrl()}${u.avatar}` : null}
                      size={84}
                      style={{
                        border: `3px solid ${cols.accent}`,
                        backgroundColor: token.colorFillAlter,
                      }}
                    />
                    <span className="bd-today-badge">🎁</span>
                  </div>

                  {/* Nombre */}
                  <div className="bd-today-name">
                    {u.name}{isMe ? " · Tú" : ""}
                  </div>
                  <div className="bd-today-date">
                    {dayjs(u.birthdate).format("D [de] MMMM")}
                  </div>

                  {/* Pill */}
                  <span
                    className="bd-today-pill"
                    style={{
                      background: cols.accent,
                      color: "#fff",
                    }}
                  >
                    ¡Feliz cumple!
                  </span>

                  {/* Edad */}
                  <div className="bd-today-age">{age} años 🎈</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── POR MES ── */}
      {monthOrder.length === 0 && todayBirthdays.length === 0 && (
        <div className="bd-empty">
          <div className="bd-empty-icon">🎂</div>
          No hay cumpleaños registrados.
        </div>
      )}

      {monthOrder.map((month) => (
        <div key={month} className="bd-month-block">
          <div className="bd-section-label">
            <CalendarOutlined style={{ fontSize: 12 }} />
            <span className="bd-month-title">{month.charAt(0).toUpperCase() + month.slice(1)}</span>
          </div>

          <div className="bd-person-grid">
            {byMonth[month].map((u, i) => {
              const age      = calcAge(u.birthdate, today);
              const days     = daysUntil(u.birthdate, today);
              const isSoon   = days <= 7;
              const isMe     = String(u.id) === String(currentUserId);

              return (
                <div
                  key={u.id}
                  className="bd-person-card"
                  style={{ animationDelay: `${i * 0.04}s` }}
                >
                  <Avatar
                    src={u.avatar ? `${getBackendUrl()}${u.avatar}` : null}
                    size={52}
                    style={{
                      border: `2px solid ${isSoon ? token.colorWarning : token.colorBorderSecondary}`,
                      backgroundColor: token.colorFillAlter,
                      flexShrink: 0,
                    }}
                  />

                  <div className="bd-person-name" title={u.name}>
                    {u.name}{isMe ? " · Tú" : ""}
                  </div>

                  <div className="bd-person-date">
                    {dayjs(u.birthdate).format("D [de] MMM")} · {age} años
                  </div>

                  <span className={`bd-days-pill ${isSoon ? "bd-days-soon" : "bd-days-normal"}`}>
                    {days === 1
                      ? "¡Mañana! 🎈"
                      : days === 0
                      ? "¡Hoy! 🎂"
                      : `${days} días`}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
